import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Award, Users, ShieldCheck } from 'lucide-react';
import { useStoreData } from '../hooks/useStoreData';
import { useCart } from '../contexts/CartContext';
import Header from '../components/Header';
import Cart from '../components/Cart';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const AboutUs = () => {
  const navigate = useNavigate();
  const { settings } = useStoreData();
  const { getCartCount } = useCart();
  const [isCartOpen, setIsCartOpen] = useState(false);

  const values = [
    {
      icon: <Heart size={40} />,
      title: "Quality Craftsmanship",
      description: "Every abaya is meticulously crafted with attention to detail, using premium fabrics and traditional techniques passed down through generations."
    },
    {
      icon: <Award size={40} />,
      title: "Excellence in Service",
      description: "We pride ourselves on exceptional customer service, ensuring your shopping experience is as elegant as our products."
    },
    {
      icon: <Users size={40} />,
      title: "Community First",
      description: "Building lasting relationships with our customers and supporting the modest fashion community is at the heart of what we do."
    },
    {
      icon: <ShieldCheck size={40} />,
      title: "Trust & Integrity",
      description: "Transparency and honesty in all our dealings. We stand behind every product we sell with complete integrity."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="About Us - Premium Abaya Store | Rizzahra"
        description="Learn about Rizzahra's heritage in modest fashion since 2025. Discover our commitment to quality craftsmanship, excellence, and the Islamic fashion community."
        keywords="about rizzahra, modest fashion, islamic clothing heritage, quality abayas"
        url="/about-us"
      />
      {/* Main Header */}
      <Header
        cartCount={getCartCount()}
        onCartClick={() => setIsCartOpen(true)}
        onAdminClick={() => navigate('/admin')}
      />

      {/* Page Header */}
      <header className="bg-[#4a1a1f] text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold uppercase tracking-wider">About Us</h1>
          <p className="text-gray-300 mt-2">Crafting elegant modest fashion for the modern woman</p>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        {/* Introduction */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h2 className="text-3xl font-bold text-[#4a1a1f] mb-6">
              Welcome to {settings?.storeName || 'Rizzahra'}
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                At {settings?.storeName || 'Rizzahra'}, we believe that modest fashion is not just about clothing—it's a
                statement of grace, elegance, and personal values. Since our establishment, we have been dedicated
                to providing women across Pakistan with beautiful, high-quality abayas that combine traditional
                modesty with contemporary style.
              </p>
              <p>
                Our journey began with a simple vision: to make premium modest wear accessible to every woman
                who values both faith and fashion. Today, we are proud to be a trusted name in the modest fashion
                industry, serving thousands of satisfied customers who trust us for their wardrobe essentials.
              </p>
              <p>
                Every piece in our collection is carefully selected and crafted to meet the highest standards of
                quality, comfort, and style. We work with skilled artisans and use only the finest fabrics to ensure
                that each abaya you purchase from us is a treasured addition to your wardrobe.
              </p>
            </div>
          </div>
        </div>

        {/* Our Values */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-[#4a1a1f] text-center mb-4 uppercase tracking-wider">
            Our Values
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            The principles that guide everything we do
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-6 text-center hover:shadow-md transition-shadow">
                <div className="text-[#4a1a1f] flex justify-center mb-4">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{value.title}</h3>
                <p className="text-gray-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* What Sets Us Apart */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-[#4a1a1f] text-center mb-10 uppercase tracking-wider">
            What Sets Us Apart
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-bold text-[#4a1a1f] mb-3">Premium Quality Fabrics</h3>
              <p className="text-gray-700">
                We source the finest materials including premium Nida, Crepe, and Korean fabrics, ensuring
                your abayas are comfortable, durable, and luxurious.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-bold text-[#4a1a1f] mb-3">Affordable Luxury</h3>
              <p className="text-gray-700">
                High quality doesn't have to mean high prices. We believe every woman deserves access to
                beautiful modest fashion without breaking the bank.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-bold text-[#4a1a1f] mb-3">Wide Range of Styles</h3>
              <p className="text-gray-700">
                From everyday essentials to special occasion pieces, our diverse collection ensures you'll
                find the perfect abaya for any moment in your life.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-bold text-[#4a1a1f] mb-3">Customer-Centric Approach</h3>
              <p className="text-gray-700">
                Your satisfaction is our priority. We offer free delivery across Pakistan, easy exchanges,
                and dedicated customer support to ensure a seamless shopping experience.
              </p>
            </div>
          </div>
        </div>

        {/* Our Promise */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#4a1a1f] text-white rounded-lg p-8 text-center">
            <h2 className="text-3xl font-bold mb-4 uppercase tracking-wider">Our Promise to You</h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              We promise to continue delivering exceptional quality, unmatched service, and timeless designs
              that honor both tradition and modernity. Your trust in us drives our commitment to excellence,
              and we're grateful to be part of your modest fashion journey.
            </p>
            <p className="text-gray-300 italic">
              "Elegance is not about being noticed, it's about being remembered."
            </p>
          </div>
        </div>
      </div>

      <Footer />
      <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  );
};

export default AboutUs;
